import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { SCANNERS } from "../../data/content";
import { Reveal } from "./Reveal";
import { PhoneMockup, ScrollPhoneMockup, VideoPhoneMockup } from "./PhoneMockup";
import { useScannerDetail } from "./ScannerDetail";
import { BrandLabel, renderBrandPhrases } from "./BrandLabel";
import scanScroll from "../../assets/scan-result-scroll.webp";
import scanScrollBody from "../../assets/scan-result-scroll-body.webp";

const SCROLL_SRC = { food: scanScroll, body: scanScrollBody };
const VIDEO_SRC = { food: "/food-scan-video.mp4", body: "/body-scan-video.mp4" };

const Check = ({ color }) => (
  <span
    className="mt-1 flex h-4 w-4 flex-none items-center justify-center rounded-full"
    style={{ backgroundColor: color }}
  >
    <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" fill="none">
      <path d="M2.5 6.2l2 2 4.5-4.6" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

const ScannerCard = ({ s, delay }) => {
  const { openDetail } = useScannerDetail();
  const showDetail = s.detail && !s.hideCardDetails;
  const open = () => showDetail && openDetail(s.key);

  return (
  <motion.article
    data-testid={`scanner-card-${s.key}`}
    onClick={open}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
    className={`group flex flex-col rounded-3xl bg-white border border-line p-6 md:p-7 shadow-soft transition-shadow duration-500 hover:shadow-soft-lg ${
      showDetail ? "cursor-pointer" : ""
    }`}
  >
    <div
      className="relative flex items-center justify-center rounded-2xl px-6 pt-8 pb-0 overflow-hidden h-64"
      style={{ backgroundColor: s.bg }}
    >
      <div className="translate-y-6 transition-transform duration-500 group-hover:-translate-y-0 w-[150px]">
        {VIDEO_SRC[s.key] ? (
          <VideoPhoneMockup
            src={`${process.env.PUBLIC_URL}${VIDEO_SRC[s.key]}`}
            className="!w-full"
          />
        ) : SCROLL_SRC[s.key] ? (
          <ScrollPhoneMockup
            src={SCROLL_SRC[s.key]}
            alt={`${s.name} scan result`}
            className="!w-full"
            duration={s.key === "body" ? 30 : 34}
          />
        ) : (
          <PhoneMockup src={s.screenshot} alt={`${s.name} scanner screen`} className="!w-full" />
        )}
      </div>
    </div>

    <div className="mt-6 flex items-center gap-2">
      <span
        className="rounded-full px-3 py-1 text-xs font-semibold"
        style={{ backgroundColor: s.bg, color: s.text }}
      >
        {s.short}
      </span>
    </div>

    <h3
      data-testid={`scanner-name-${s.key}`}
      className="mt-3 text-2xl font-bold tracking-tight text-ink w-fit"
    >
      <BrandLabel text={s.name} />
    </h3>
    <p className="mt-2 text-[15px] leading-relaxed text-slate">{renderBrandPhrases(s.tagline)}</p>

    {s.pointsLabel && (
      <p className="mt-5 text-xs font-semibold uppercase tracking-[0.15em] text-slate">
        {s.pointsLabel}
      </p>
    )}
    <ul className={`space-y-3 flex-1 ${s.pointsLabel ? "mt-3" : "mt-5"}`}>
      {s.points.map((p, i) => (
        <li key={i} className="flex gap-3 text-[15px] text-ink/80">
          <Check color={s.accent} />
          <span>{p}</span>
        </li>
      ))}
    </ul>

    <div className="mt-6 pt-5 border-t border-line flex items-center justify-end gap-3">
      {showDetail && (
        <button
          data-testid={`learn-more-${s.key}`}
          onClick={(e) => {
            e.stopPropagation();
            openDetail(s.key);
          }}
          className="group/link inline-flex items-center gap-1.5 text-sm font-semibold transition-colors"
          style={{ color: s.text }}
        >
          See details
          <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover/link:translate-x-0.5" />
        </button>
      )}
    </div>
  </motion.article>
  );
};

export const Scanners = () => (
  <section id="scanners" className="relative py-20 md:py-28">
    <div className="mx-auto max-w-[1240px] px-5 md:px-8">
      <div className="max-w-2xl mb-12">
        <Reveal>
          <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate mb-3">
            Two smart scanners — Buy one or both
          </p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-ink leading-[1.1]">
            Snap, scan, understand
          </h2>
        </Reveal>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
        {SCANNERS.map((s, i) => (
          <ScannerCard key={s.key} s={s} delay={i * 0.1} />
        ))}
      </div>
    </div>
  </section>
);
